export type HelpSection = {
  id: string;
  title: string;
  summary: string;
  bullets?: string[];
  steps?: string[];
  tips?: string[];
};

export type HelpFaqItem = {
  question: string;
  answer: string;
};

export type HelpDocumentation = {
  title: string;
  subtitle: string;
  intro: string[];
  quickStart: {
    title: string;
    steps: string[];
  };
  sections: HelpSection[];
  faq: HelpFaqItem[];
  support: {
    title: string;
    bullets: string[];
  };
};

export const helpDocumentation: HelpDocumentation = {
  title: "Help & System Guide",
  subtitle: "",
  intro: [
    "This system runs a public website and a private admin dashboard.",
    "If you have a technical background, think of it as a light CMS backed by a database with permissions.",
  ],
  quickStart: {
    title: "Quick start for admins",
    steps: [
      "Go to /admin/login and sign in with an admin account.",
      "Check the connectivity status banner at the top of the dashboard.",
      "Update Organization details (name, logo, contact info).",
      "Choose a layout theme and navigation style.",
      "Create or edit Pages, then publish.",
      "Review the public site to confirm changes.",
    ],
  },
  sections: [
    {
      id: "overview",
      title: "System overview",
      summary: "The public site shows content to visitors. The admin dashboard is where staff create, edit, and publish content.",
      bullets: [
        "Public pages include events, news, gallery, projects, and contact forms when enabled.",
        "The admin dashboard is protected by login at /admin/dashboard.",
        "Content updates appear after you save and publish.",
      ],
    },
    {
      id: "navigation-layout",
      title: "Navigation and layout",
      summary: "Navigation can be a top bar, sidebar, collapsible menu, centered split, or mega menu. Admins choose the style.",
      bullets: [
        "Navigation links come from published pages.",
        "The Help link stays in the top right on each navigation style.",
      ],
    },
    {
      id: "finding-info",
      title: "Finding information",
      summary: "Visitors can use navigation, search, and feature buttons to reach key sections.",
      bullets: [
        "Use the Search page to find pages, news, events, and other content.",
        "Event, membership, and donation buttons appear only when enabled.",
      ],
    },
    {
      id: "admin-access",
      title: "Admin access and roles",
      summary: "Admins log in at /admin/login. Roles and permissions control which tools you can use.",
      bullets: [
        "If a menu item is missing, your account may not have permission.",
        "Use the Sign out button when you are done.",
      ],
    },
    {
      id: "pages-content",
      title: "Pages and content blocks",
      summary: "Pages are built from blocks like headings, text, images, and buttons.",
      steps: [
        "Open Admin → Pages.",
        "Create or edit a page.",
        "Use the editor to add blocks and save.",
        "Publish the page to show it in navigation.",
        "Open the public page to verify.",
      ],
      bullets: [
        "Keep headings short and clear.",
        "Add alt text for important images.",
      ],
    },
    {
      id: "events-news",
      title: "Events, news, and updates",
      summary: "These sections manage time-based or featured content for visitors.",
      bullets: [
        "Events include dates and optional registrations; they appear on the Events calendar.",
        "News and testimonials highlight stories and updates.",
        "Projects and daily facts showcase impact and key information.",
      ],
    },
    {
      id: "media-gallery",
      title: "Media and gallery",
      summary: "The Gallery is the source for images across the site.",
      bullets: [
        "Upload photos in the Gallery first, then select them for hero images or logos.",
        "Use clear filenames and keep file sizes reasonable.",
        "If an image fails to load, re-upload it or check the file path.",
      ],
    },
    {
      id: "forms-submissions",
      title: "Forms and submissions",
      summary: "Forms capture visitor data and route it to the dashboard.",
      bullets: [
        "Use Forms to create custom forms and fields.",
        "Contact and quote request submissions appear in the dashboard.",
        "Export submissions when you need to share them.",
      ],
    },
    {
      id: "donations-membership",
      title: "Donations and membership",
      summary: "Track giving and membership applications in one place.",
      bullets: [
        "Donation records include payment status and donor details.",
        "Membership applications can be reviewed and updated.",
      ],
    },
    {
      id: "settings-seo-analytics",
      title: "Settings, SEO, and analytics",
      summary: "Settings control the site identity and visibility in search.",
      bullets: [
        "Organization details update the site name, logo, and contact info.",
        "SEO tools set page titles and descriptions.",
        "Analytics dashboards show traffic and search behavior.",
      ],
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      summary: "If something looks off, run these quick checks.",
      steps: [
        "If you see a connectivity warning, the database is not configured or offline.",
        "If changes are missing, confirm the page is published and refresh the site.",
        "If a feature section is missing, confirm it is enabled in settings.",
        "If you cannot access a tool, ask for the correct role or permission.",
      ],
    },
  ],
  faq: [
    {
      question: "How do I add a new page to the navigation?",
      answer: "Create the page in Admin → Pages, publish it, and ensure it is set to show in navigation.",
    },
    {
      question: "Why don't my changes show on the public site?",
      answer: "Check that the content is published, then refresh the page. If the issue continues, verify connectivity in the admin banner.",
    },
    {
      question: "Why can't I see certain admin tools?",
      answer: "Your account may not have the required role or permissions. Ask an admin to update access.",
    },
    {
      question: "What if I see a database or connectivity warning?",
      answer: "The system needs database configuration. Ask your technical admin to confirm environment settings and connectivity.",
    },
  ],
  support: {
    title: "Need more help?",
    bullets: [
      "Use the Contact page or your internal support channel.",
      "Share the page URL, steps to reproduce, and what you expected to see.",
      "Include a screenshot or error message if possible.",
    ],
  },
};
